import { type FileMessageContent, type GifAttachment } from "@fitness-app/data-models/entities/Chat";
import type { TFunction } from "i18next";

/**
 * How a browser should play a GIF bubble (TRE-2117).
 *
 * A bubble starts on its still frame and only loops once the viewer starts it, the way Facebook
 * plays GIFs — a conversation full of bubbles animating on their own is what made this worth
 * changing.
 *
 * An animated image in an `<img>` cannot be paused on its current frame: the browser owns the
 * animation clock and exposes no control over it. KLIPY ships an mp4 rendition alongside every
 * GIF, so where we have one the bubble is really a muted looping `<video>`, which stops exactly
 * where the viewer stopped it.
 *
 * `image` is the fallback for attachments without an mp4 (a message sent before the picker asked
 * for that format, or a provider result that lacked it). There, stopping can only swap in the
 * still preview, so a restart begins from the first frame — and with no still to swap to there is
 * nothing to stop at all.
 */
export type GifPlaybackSource =
  | { kind: "video"; videoUrl: string; posterUrl: string | null }
  | { kind: "image"; animatedUrl: string; stillUrl: string | null };

export const getGifPlaybackSource = (
  file: Pick<FileMessageContent, "url"> & { gif: Pick<GifAttachment, "mp4Url" | "previewUrl"> },
): GifPlaybackSource => {
  const previewUrl = file.gif.previewUrl ?? null;

  if (file.gif.mp4Url) {
    return { kind: "video", videoUrl: file.gif.mp4Url, posterUrl: previewUrl };
  }

  return { kind: "image", animatedUrl: file.url, stillUrl: previewUrl };
};

/** An `image` bubble with no still frame has nothing to freeze on, so it stays a plain looping GIF. */
export const canPauseGifPlayback = (source: GifPlaybackSource): boolean =>
  source.kind === "video" || Boolean(source.stillUrl);

/** Screen-reader label for the play/pause control, shared so the three apps read identically. */
export const getGifPlaybackLabel = (tUi: TFunction, { playing, name }: { playing: boolean; name: string }): string =>
  `${playing ? tUi<string>("uiCommunication:stop") : tUi<string>("uiCommunication:play")} GIF: ${name}`;
